import React, { useEffect, useRef, useState } from "react";
import { ConsumableIcon } from "../../components/ItemIcon";
import { Tooltip } from "../../components/Tooltip";
import { CLAMP_NAME, clampsInUse } from "../../game/Clamp";
import { CONSUMABLE_TYPES, ConsumableId } from "../../game/Consumable";
import { formatCount } from "../../utils/formatNumber";
import { BAR_CLAMP_ICON } from "../../utils/uiImages";
import { RewardFlights } from "../RewardFlights";
import { useGame, useShopState } from "../useShell";
import {
  REWARD_TARGET_ATTRIBUTE,
  RewardTarget,
  supplyTarget,
} from "./payout/rewardTargets";

/**
 * The supply cabinet's running tally, hanging under the top bar's right
 * end: the word "Supplies", and under it — once it's clicked open — one
 * row per kind on hand, an icon and a count, floating straight over the
 * world with no card around them. The name is the row's tooltip. It
 * starts closed and remembers whether you left it open. Hidden entirely
 * while the cabinet is empty so the early game stays quiet, and while
 * you're away on a trip.
 *
 * It stays up during bench work (it's shop chrome, above the bench
 * view), so it publishes its footprint as `--supplies-clearance` on the
 * root element for the bench's plan drawer to start its right column
 * below.
 *
 * It is also where a granted supply flies to (`RewardFlightLayer`): each
 * row is the landing spot for its own kind, and the word "Supplies" is
 * the spot while the tally is folded. A kind with a flight in the air
 * reads short by what's still flying and catches up as it lands — the
 * sim's count changed at the commit, this is the readout keeping pace
 * with the picture. Everything else reads live.
 */

const OPEN_KEY = "woodworking-tycoon-supplies-open";

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === "true";
  } catch {
    return false;
  }
}

/** Floating text over the world canvas needs its own shadow to read. */
const FLOATING_TEXT = "drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]";

export const SuppliesSection: React.FC = () => {
  const game = useGame();
  const gameState = useShopState();
  const flights = game.entities.tryGetSingleton(RewardFlights);
  const [open, setOpen] = useState(readOpen);
  const sectionRef = useRef<HTMLElement>(null);

  const stocked = (Object.keys(CONSUMABLE_TYPES) as ConsumableId[]).filter(
    (id) => (gameState.consumables[id] ?? 0) > 0,
  );
  const clampsOwned = gameState.clamps;
  const clampsHeld = clampsInUse(gameState.machines);
  const visible =
    !gameState.player.away && (stocked.length > 0 || clampsOwned > 0);

  useEffect(() => {
    const root = document.documentElement;
    const el = sectionRef.current;
    if (!el) {
      root.style.setProperty("--supplies-clearance", "0px");
      return;
    }
    const observer = new ResizeObserver(() => {
      // The 12px matches the HUD's space-y-3 rhythm
      root.style.setProperty(
        "--supplies-clearance",
        `${el.offsetHeight + 12}px`,
      );
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty("--supplies-clearance", "0px");
    };
  }, [visible, open]);

  if (!visible) return null;

  const toggle = () => {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      try {
        localStorage.setItem(OPEN_KEY, String(next));
      } catch {
        // Private-mode storage failures just lose the preference
      }
      return next;
    });
  };

  return (
    <section
      ref={sectionRef}
      className="pointer-events-auto flex flex-col items-end gap-1.5"
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={`flex items-center gap-2 font-condensed text-[0.65rem] uppercase tracking-[0.2em] text-paper-manila/70 hover:text-paper-manila ${FLOATING_TEXT}`}
      >
        <span {...{ [REWARD_TARGET_ATTRIBUTE]: "supplies" }}>Supplies</span>
        <span aria-hidden>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <ul className="flex flex-col items-end gap-1.5">
          {stocked.map((id) => {
            const type = CONSUMABLE_TYPES[id];
            // Short by what's still flying to this row; see the header.
            const amount =
              gameState.consumables[id] - (flights?.supplyInFlight(id) ?? 0);
            return (
              <SupplyRow
                key={id}
                target={supplyTarget(id)}
                name={type.name}
                icon={
                  <ConsumableIcon
                    consumableId={id}
                    className="size-7 shrink-0 [image-rendering:pixelated]"
                  />
                }
                // "8" for nails (the icon already says what they are),
                // "16 oz" for measured goods
                count={
                  type.unit === type.name.toLowerCase()
                    ? formatCount(amount)
                    : `${formatCount(amount)} ${type.unit}`
                }
              />
            );
          })}
          {/* Clamps aren't spent, so the tally reads as a rack count:
              how many you own, and how many are holding a glue-up */}
          {clampsOwned > 0 && (
            <SupplyRow
              name={`${CLAMP_NAME}s`}
              icon={
                <img
                  src={BAR_CLAMP_ICON}
                  alt=""
                  draggable={false}
                  className="size-7 shrink-0 select-none [image-rendering:pixelated]"
                />
              }
              count={
                clampsHeld > 0
                  ? `${formatCount(clampsOwned - clampsHeld)} of ${formatCount(clampsOwned)} free`
                  : formatCount(clampsOwned)
              }
            />
          )}
        </ul>
      )}
    </section>
  );
};

/** One kind on hand: its icon and how much, named only on hover. The
 * count is the reward target (`inline-block`, so the landing thump's
 * scale has a box to act on). */
const SupplyRow: React.FC<{
  target?: RewardTarget;
  name: string;
  icon: React.ReactNode;
  count: string;
}> = ({ target, name, icon, count }) => (
  <Tooltip content={name} placement="left">
    <li
      aria-label={`${name}: ${count}`}
      className={`flex items-center gap-2 ${FLOATING_TEXT}`}
    >
      {icon}
      <span
        {...(target ? { [REWARD_TARGET_ATTRIBUTE]: target } : {})}
        className="inline-block font-condensed text-sm tabular-nums text-paper-manila"
      >
        {count}
      </span>
    </li>
  </Tooltip>
);
